import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { PracticeChallenge } from "@/components/PracticeChallenge";
import { Insight } from "@/components/Insight";
import { Keyboard } from "@/components/Keyboard";
import {
  Tip, KeyIdea, Pull, Steps, Step, Vocab, Term, NextUp,
  KeyHighlights, Highlight,
} from "@/components/Callouts";

export default function PianoFoundation29() {
  const { lesson, course, accent } = findLesson("piano-foundation", "29");
  return (
    <LessonShell
      course={course}
      courseSlug="piano-foundation"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <KeyHighlights>
        <Highlight>Don't memorise 24 chords. Memorise <strong>shapes</strong>. Major chords come in three groups: <strong>WWW</strong>, <strong>WBW</strong>, <strong>BWB</strong> — plus a few unique shapes.</Highlight>
        <Highlight>Major <strong>WWW</strong> = C, F, G. Major <strong>WBW</strong> = D, A, E. Major <strong>BWB</strong> = D♭, E♭, A♭.</Highlight>
        <Highlight>The "unique" majors are the <strong>B's</strong> — B major (WBB), B♭ major (BWW) — plus <strong>F♯ major</strong>, which is all-black (BBB).</Highlight>
        <Highlight>Minor chords flip the shape category: WWW majors (C, F, G) become WBW minors. WBW majors (D, A, E) become WWW minors. BWB stays BWB.</Highlight>
        <Highlight>All the same-shape chords sit together on the <strong>Circle of Fifths</strong> — neighbours on the circle share their visual shape. F♯ major and D♯ minor (its relative minor) are both all-black BBB.</Highlight>
      </KeyHighlights>

      <Insight eyebrow="Why shapes" title="Memorise three patterns instead of 24 chords" accent={accent.hex}>
        <Pull>
          A good way to form them is just using the shapes of the chords and trying to
          group those shapes together.
        </Pull>

        <p>
          Beginners try to memorise 12 major chords and 12 minor chords as 24 separate
          things. Pros don't. They group them by visual shape. Three or four shape categories
          cover everything — you learn the categories, then map the chord into the right
          one.
        </p>

        <Vocab>
          <Term name="WWW" def="three white keys. The chord lives entirely on white notes." />
          <Term name="WBW" def="white-black-white. White root and top, black middle." />
          <Term name="BWB" def="black-white-black. Black root and top, white middle." />
          <Term name="BBB" def="all-black. The unique 'triple B' shape." />
        </Vocab>
      </Insight>

      <Insight eyebrow="Major chord shapes" title="Three groups · plus the unique B-family" accent={accent.hex}>
        <div className="space-y-3">
          <div className="rounded-xl border border-emerald-500/40 bg-emerald-500/5 p-4">
            <div className="text-xs font-bold uppercase tracking-wider text-emerald-300">WWW · all white</div>
            <div className="mt-1 font-mono text-[14px] text-nsm-ink">C major · F major · G major</div>
            <div className="mt-1 text-[12px] text-nsm-dim">These are the I-IV-V of C major — they all live together as the same shape.</div>
          </div>
          <div className="rounded-xl border border-blue-500/40 bg-blue-500/5 p-4">
            <div className="text-xs font-bold uppercase tracking-wider text-blue-300">WBW · white-black-white</div>
            <div className="mt-1 font-mono text-[14px] text-nsm-ink">D major · A major · E major</div>
            <div className="mt-1 text-[12px] text-nsm-dim">These are the I-IV-V of A major. Same shape, sandwiched.</div>
          </div>
          <div className="rounded-xl border border-purple-500/40 bg-purple-500/5 p-4">
            <div className="text-xs font-bold uppercase tracking-wider text-purple-300">BWB · black-white-black</div>
            <div className="mt-1 font-mono text-[14px] text-nsm-ink">D♭ major · E♭ major · A♭ major</div>
            <div className="mt-1 text-[12px] text-nsm-dim">These are the I-IV-V of A♭ major.</div>
          </div>
          <div className="rounded-xl border border-amber-500/40 bg-amber-500/5 p-4">
            <div className="text-xs font-bold uppercase tracking-wider text-amber-300">Unique shapes</div>
            <ul className="mt-1 space-y-1 text-[14px] font-mono text-nsm-ink">
              <li><strong>B major</strong> = WBB (white-black-black). B-D♯-F♯.</li>
              <li><strong>B♭ major</strong> = BWW (black-white-white). B♭-D-F.</li>
              <li><strong>F♯ major</strong> = BBB (triple black). F♯-A♯-C♯.</li>
            </ul>
          </div>
        </div>

        <KeyIdea>
          The B's are always unique. B major and B♭ major fall outside the three-group
          system. F♯ major is the all-black "triple B" — and its relative minor (D♯ minor)
          is also triple-black, the only minor chord that lives there.
        </KeyIdea>
      </Insight>

      <Insight title="The three shapes on the keyboard" accent={accent.hex}>
        <Keyboard
          startOctave={3}
          octaves={2}
          highlights={[
            { pitch: "C4", color: "#22c55e", label: "C" },
            { pitch: "E4", color: "#22c55e", label: "WWW" },
            { pitch: "G4", color: "#22c55e", label: "" },
            { pitch: "D4", color: "#3b82f6", label: "D" },
            { pitch: "F#4", color: "#3b82f6", label: "WBW" },
            { pitch: "A4", color: "#3b82f6", label: "" },
            { pitch: "Db5", color: "#a855f7", label: "D♭" },
            { pitch: "F5", color: "#a855f7", label: "BWB" },
            { pitch: "Ab5", color: "#a855f7", label: "" },
          ]}
          height={200}
        />

        <PracticeChallenge
          courseSlug="piano-foundation"
          order="29"
          id="cmajor-www"
          mode="chord"
          title="C major · the WWW shape"
          prompt="Play the C major chord — all three white keys together: C, E, G."
          notes={["C4", "E4", "G4"]}
          startOctave={3}
          octaves={3}
        />

        <p className="prose-nsm text-sm">
          <span className="text-emerald-300">Green</span> = C major (WWW).{" "}
          <span className="text-blue-300">Blue</span> = D major (WBW).{" "}
          <span className="text-purple-300">Purple</span> = D♭ major (BWB). Once you've
          felt these three shapes, every other major triad falls into one of them — except
          the B-family.
        </p>
      </Insight>

      <Insight eyebrow="Minor chord shapes" title="The shape categories swap" accent={accent.hex}>
        <p>
          When you go from major to minor, the visual shapes shuffle:
        </p>

        <div className="overflow-x-auto rounded-xl border border-nsm-border bg-nsm-card/50 p-4 text-[13px]">
          <table className="w-full">
            <thead>
              <tr className="text-nsm-dim">
                <th className="text-left">Shape</th>
                <th>Major chords here</th>
                <th>Minor chords here</th>
              </tr>
            </thead>
            <tbody className="font-mono">
              <tr className="border-t border-nsm-border"><td>WWW</td><td>C · F · G</td><td className="text-rose-300">A · D · E (minor)</td></tr>
              <tr className="border-t border-nsm-border"><td>WBW</td><td>D · A · E</td><td className="text-rose-300">C · F · G (minor)</td></tr>
              <tr className="border-t border-nsm-border"><td>BWB</td><td>D♭ · E♭ · A♭</td><td className="text-rose-300">C♯ · G♯ · F♯ (minor)</td></tr>
            </tbody>
          </table>
        </div>

        <p>
          C major is WWW. C minor is WBW (because the middle E becomes E♭). D major is WBW;
          D minor is WWW. The categories don't disappear — they swap.
        </p>

        <Pull>
          That would be C minor, F minor, G minor. Basically all the major chords which
          were WWW now end up becoming WBW.
        </Pull>
      </Insight>

      <Insight eyebrow="The unique minors" title="The B's again · plus E♭ minor as triple-black" accent={accent.hex}>
        <ul className="space-y-1 text-[14px]">
          <li><strong>B minor</strong> = WWB (white-white-black). B-D-F♯.</li>
          <li><strong>B♭ minor</strong> = BBW (black-black-white). B♭-D♭-F.</li>
          <li><strong>D♯ minor (= E♭ minor)</strong> = BBB (triple black). The minor twin of F♯ major — they're each other's relative.</li>
        </ul>

        <Tip>
          A useful relationship: F♯ major (BBB) and D♯ minor (BBB) are <strong>relatives of
          each other</strong>. Both are triple-black. Same notes, different starting point.
          Once you've internalised one, the other is essentially free.
        </Tip>
      </Insight>

      <Insight eyebrow="The Circle of Fifths" title="Same-shape chords sit together" accent={accent.hex}>
        <p>
          On the Circle of Fifths, neighbouring chords share their shape category. C, F, G
          sit next to each other on the circle — and they're all WWW. D, A, E sit together
          and are all WBW. D♭, E♭, A♭ sit together and are all BWB. The unique shapes (B's
          and F♯) are scattered around the circle as outliers.
        </p>

        <p>
          Draw the Circle of Fifths once — clockwise C, G, D, A, E, B, F♯; counter-clockwise
          C, F, B♭, E♭, A♭, D♭, G♭ — and circle the same-shape groups. From that diagram,
          chord recall becomes pattern recognition rather than note-by-note memorisation.
        </p>
      </Insight>

      <Insight eyebrow="Practice path" title="Drill the shapes blind" accent={accent.hex}>
        <Steps>
          <Step>Write the Circle of Fifths once. Circle the WWW group. Circle the WBW group. Circle the BWB group. Box the unique chords (B's, F♯).</Step>
          <Step>Drill WWW majors first — C, F, G. Play each one root position, eyes closed. Trust your hand.</Step>
          <Step>Drill WBW majors next — D, A, E.</Step>
          <Step>Drill BWB majors — D♭, E♭, A♭.</Step>
          <Step>Drill the unique majors — B, B♭, F♯.</Step>
          <Step>Repeat the whole loop for minors. The same shapes apply, just shuffled.</Step>
        </Steps>

        <Pull>
          You could also practise playing it blind.
        </Pull>

        <p>
          Don't look. Your fingers need to be confident, and you have to trust your hands.
        </p>
      </Insight>

      <NextUp
        to="/lesson/piano-foundation-30"
        label="Lesson 30 · Chord Inversion Practice (Part 1)"
        why="Now that the chord shapes are visible, the next two lessons drill the inversions of every triad — root, first, second — across multiple keys."
      />
    </LessonShell>
  );
}
